import React, { useState } from 'react';
import { ContentLayout } from 'components/Layout';
import { useAuth } from 'lib/auth';
import { useStatistics } from '../api/getStatistics';
import { SalesGraph } from '../components/SalesGraph';
import { RevenueDonut } from '../components/RevenueDonut';
import { TrafficLine } from '../components/TrafficLine';
import { SkeletonCard } from '../components/SkeletonCard';
import CountUp from 'react-countup';

export const Dashboard = () => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState('Last 7 Days');
  const [orderFilter, setOrderFilter] = useState('all');

  const { data, isLoading } = useStatistics();
  const stats = data?.data ?? null;

  // --- Corrected Order Filtering ---
  const filteredOrders =
    stats?.recentOrders?.filter((o) => {
      if (orderFilter === 'high') return o.totalAmount > 1_000_000;
      if (orderFilter === 'low') return o.totalAmount <= 1_000_000;
      return true;
    }) ?? [];

  return (
    <ContentLayout className="pb-20">
      {/* HEADER */}
      <div className="mb-10">
        <h1 className="just-me text-2xl md:text-3xl font-semibold text-slate-700 tracking-tight">
          Welcome back, <span className="text-indigo-600">{user?.name}</span>
        </h1>
        <p className="text-sm text-slate-500 mt-1">Your business overview at a glance.</p>
      </div>

      {/* LOADING STATE */}
      {isLoading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <div className="bg-white shadow rounded-2xl p-6 mb-10">
            <div className="animate-pulse h-72 bg-gray-100 rounded-xl" />
          </div>
        </>
      )}

      {/* MAIN CONTENT */}
      {!isLoading && stats && (
        <>
          {/* TOP STATS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatCard title="Unique Visitors" value={stats.uniqueVisitors} />
            <StatCard title="Users" value={stats.totalUsers} />
            <StatCard title="Orders" value={stats.totalOrders} />
            <StatCard title="Total Revenue" value={stats.totalSales} money />
          </div>

          {/* GRAPHS SECTION */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            {/* Revenue Donut */}
            <Card>
              <SectionHeader title="Revenue Breakdown" />
              <RevenueDonut value={stats.totalSales} />
            </Card>

            {/* Traffic */}
            <Card>
              <SectionHeader title="Traffic (Unique Visitors)" />
              <TrafficLine data={stats.salesStats} />
            </Card>
          </div>

          {/* RECENT ORDERS */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-slate-800">Recent Orders</h2>
                <p className="text-sm text-slate-500">Latest customer purchases</p>
              </div>

              <select
                value={orderFilter}
                onChange={(e) => setOrderFilter(e.target.value)}
                className="border border-slate-300 rounded-lg p-2 text-sm focus:ring-indigo-500"
              >
                <option value="all">All Orders</option>
                <option value="high">High Value (&gt; UGX 1,000,000)</option>
                <option value="low">Low Value (&lt;= UGX 1,000,000)</option>
              </select>
            </div>

            <Card>
              {filteredOrders.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {filteredOrders.map((order) => (
                    <RecentOrderItem key={order._id} order={order} />
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  No recent orders match this filter.
                </div>
              )}
            </Card>
          </div>

          {/* SALES GRAPH */}
          <div className="mb-20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl md:text-2xl font-semibold text-slate-800">Sales Statistics</h2>

              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="border border-slate-300 rounded-md p-2 text-sm focus:ring-indigo-500"
              >
                <option value="Last 7 Days">Last 7 Days</option>
                <option value="Last 30 Days">Last 30 Days</option>
                <option value="This Month">This Month</option>
                <option value="This Year">This Year</option>
              </select>
            </div>

            <Card>
              <SalesGraph data={stats.salesStats} />
            </Card>
          </div>
        </>
      )}
    </ContentLayout>
  );
};

const StatCard = ({ title, value, money }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition">
    <div className="text-sm text-slate-500">{title}</div>
    <div className="text-3xl font-semibold text-slate-800 mt-2">
      <CountUp end={value} duration={1.2} separator="," prefix={money ? 'UGX ' : ''} />
    </div>
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">{children}</div>
);

const SectionHeader = ({ title }) => (
  <h3 className="text-lg font-semibold text-slate-800 mb-4">{title}</h3>
);

const RecentOrderItem = ({ order }) => {
  const amount = order.totalAmount ?? 0;

  return (
    <div className="py-4 flex flex-col md:flex-row md:items-center justify-between">
      <div className="flex flex-col">
        <span className="font-medium text-slate-800">
          Order #{order._id.slice(-5).toUpperCase()}
        </span>

        <span className="text-sm text-slate-500">
          {order.products?.length} item(s) • {order.status}
        </span>

        <span className="text-xs text-slate-400">{new Date(order.createdAt).toLocaleString()}</span>
      </div>

      <div className="text-right mt-3 md:mt-0">
        <div className="text-lg font-semibold text-slate-900">
          {new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: order.currency ?? 'UGX',
          }).format(amount)}
        </div>

        <div className="text-xs text-slate-500">{order.contactInfo?.email}</div>
      </div>
    </div>
  );
};
